// Hideo - the open video dialog (src/components/dialogs/VideoDialog.vue, logic in src/features/video.ts): its form, the
// open tab, the drafts of the other tabs (the second tab's SEO card, the "Released" tab, the "Metrics" tab and the published state),
// the scripts created or attached while it is open, and what opens, saves, deletes and closes it. The panels are
// src/components/video/ (SeoPanel.vue, PublishedPanel.vue, PublishedBox.vue, ShortButton.vue); the development
// tools reach the dialog through openVideoModal / closeVideoModal / setVideoTab (src/globals.ts).
import { defineStore } from 'pinia'
import { cleanMetrics } from '../features/metrics'
import { computed, ref, watch } from 'vue'
import { useDataStore } from './data'
import { useDialogsStore } from './dialogs'
import { useCalendarStore } from './calendar'
import { usePedStore } from './ped'
import { confirmDelete, syncToast, uiAlert, uiAlertThen, uiConfirm, uiSuccess } from './notices'
import { t, tn, tv } from '../i18n'
import { mentionHtml } from '../features/mentions'
import { newItemProject } from './projects'
import { validProject } from '../features/projects'
import { formatOfFilter } from '../features/formats'
import { pubDraftOf, pubLinkCount, setPublished, type PubDraft } from '../features/published'
import { SEO_DEF, SEO_TAB, cleanSeo, scriptHtmlById, scriptPlainById, seoChecks, type SeoVideo } from '../features/seo'
import { plain } from '../features/scripts'
import {
  addVideoScript, celebration, deleteScriptsQuestion, deleteVideo, deletedVideoScripts, formError, formKind, pickScript,
  saveVideo, scriptHasText, scriptStart, sessionOrphans, videoForm, dialogScripts, type ScriptStart, type VideoForm, type VideoPrefill
} from '../features/video'
import type { Kind, Video, VideoSeo } from '../data/types'
import { saveAppData } from '../features/storage/save'
import { esc } from '../utils/html'
import { useEditorStore } from './editor'
import { STATUS_READY } from '../data/contentTypes'
import { videoTitle } from '../features/ped'

export type VideoTab = 'details' | 'seo' | 'pub' | 'metrics'
/** The tab bar: second tab's icon, label and score, links of the "Released" tab, whether that tab is shown */
interface VideoTabsView {
  icon: string
  label: string
  n: number
  max: number
  links: number
  pub: boolean
}

export const useVideoStore = defineStore('video', () => {
  const store = useDataStore()
  const dialogs = useDialogsStore()
  /** The open dialog's form (null = closed) */
  const form = ref<VideoForm | null>(null)
  const tab = ref<VideoTab>('details')
  /** Bumped at every setTab: the panel of the tab is drawn again from the start even when the tab does not change */
  const tabSeq = ref(0)
  /** Scripts created in the dialog (deleted on close if no video took them) and attached to it (they become the video's
   *  on save) */
  const session = ref<string[]>([])
  const attached = ref<string[]>([])
  /** "Where do you want to start?" of the script picker is open */
  const scriptPanel = ref(false)
  /** Bumped when the legacy code asks to redraw the script picker (fillScriptSelect: editor closed, script deleted) */
  const scriptsTick = ref(0)
  /** Second tab: the SEO card being edited (saved as video.seo) */
  const seo = ref<VideoSeo>({ ...SEO_DEF, variants: [] })
  /** "Released" tab being edited: links, release date and time, notes (saved as pubLinks, publishedAt, pubNote) */
  const pub = ref<PubDraft>(pubDraftOf(null))
  /** "Metrics" tab being edited: verdict and numbers per platform (saved as video.metrics, src/features/metrics.ts) */
  const metrics = ref<NonNullable<Video['metrics']>>({})
  /** The open video is published (set when the dialog opens; "Mark as published" / "Back to the plan" change it and save) */
  const published = ref(false)
  /** Save was pressed without a title: the title field is shown in the main colour until something is typed */
  const missingTitle = ref(false)
  /** Bumped when the message about the missing title is closed: the dialog puts the cursor in the title */
  const titleFocus = ref(0)

  const kind = (): Kind => (form.value ? formKind(form.value) : 'h')
  /** The project chosen in the dialog, checked again (a deleted project → the project of new items) */
  const projectId = () => validProject(store.getData(), form.value?.projectId, newItemProject())

  /** Video dialog: an existing video, or a new one (null) with optional starting fields (Calendar day / time, Board column…) */
  function open(id: string | null = null, prefill?: VideoPrefill) {
    const d = store.getData()
    session.value = []
    attached.value = []
    scriptPanel.value = false
    // Format of a new video: the active format filter's, in the new video's project; its Type: the active Type filter's
    // (unless the caller chose one), so the new video stays on the filtered Board
    const pid = newItemProject(), pf = usePedStore().filter
    const pre: VideoPrefill | undefined = !id && !prefill?.format && pf.type ? { ...prefill, format: pf.type } : prefill
    form.value = videoForm(d, id, { newProject: pid, filterSeries: formatOfFilter(d, pf.series, pid), prefill: pre })
    const v = id ? d.videos.find(x => x.id === id) || null : null
    published.value = !!(id && v && v.published)
    // SEO and "Released" drafts; the dialog opens on Details
    seo.value = cleanSeo(v && v.seo)
    pub.value = pubDraftOf(v)
    metrics.value = JSON.parse(JSON.stringify(cleanMetrics(v && v.metrics)))
    missingTitle.value = false
    setTab('details')
    dialogs.show('video', { id })
  }

  /** The "Released" tab is shown for a published video or one in Ready */
  const pubVisible = () => published.value || form.value?.status === STATUS_READY
  /** The video as it is now in the Details tab (even if not saved): what the SEO checks read */
  function videoNow(): SeoVideo & { link: string } {
    const f = form.value, k = kind(), d = store.getData()
    return { title: (f?.title || '').trim(), thumbnail: f?.thumbnail || '', link: f?.link || '', kind: k,
      script: k === 'v' || k === 'blog' ? scriptPlainById(d, f?.scriptId) : '',
      scriptHtml: k === 'blog' ? scriptHtmlById(d, f?.scriptId) : '', publishDate: f?.publishDate || '', publishTime: f?.publishTime || '' }
  }
  /** The tab bar: the second tab's icon, name and score (the SEO card against the video as it is now in Details), the
   *  number of links in Released, and whether Released and Metrics are shown. Follows the form, the SEO card and the links */
  const tabs = computed<VideoTabsView>(() => {
    void store.rev
    const vn = videoNow(), checks = seoChecks(seo.value, vn), [icon, label] = SEO_TAB[vn.kind]
    return { icon, label, n: checks.filter(c => c.ok).length, max: checks.length, links: pubLinkCount(pub.value.links), pub: pubVisible() }
  })
  /** Shows a tab ('pub' only when it is visible); its panel is drawn again from the start */
  function setTab(id: VideoTab) {
    tab.value = (id === 'pub' || id === 'metrics') && !pubVisible() ? 'details' : id
    tabSeq.value++
  }
  // Released and Metrics hidden (status moved away from Ready, "Back to the plan"): back to Details
  watch(pubVisible, v => { if (!v && (tab.value === 'pub' || tab.value === 'metrics')) setTab('details') })

  /** Redraws the script picker (legacy fillScriptSelect): sel = the script to show ('' = the current one, or the first) */
  function pickScriptNow(sel = '') {
    const f = form.value
    if (!f) return
    f.scriptId = pickScript(dialogScripts(store.getData(), f.id, session.value, attached.value), sel, f.scriptId)
    scriptsTick.value++
  }
  /** "Attach here": a script without a video goes to this video (on save) */
  function attachScript(id: string) {
    if (!attached.value.includes(id)) attached.value.push(id)
    scriptPanel.value = false
    pickScriptNow(id)
  }
  /** New script from a starting point (features/video.ts scriptStart), then the editor opens on it */
  function createScript(v = '', file: ScriptStart | null = null) {
    const f = form.value
    if (!f) return
    const d = store.getData()
    const start = scriptStart(d, v, file, (title, content) => `<h2>${esc(title)}</h2><p>${mentionHtml(content, false).replace(/\n/g, '<br>')}</p>`)
    const id = addVideoScript(d, f.id, f.title, start, dialogScripts(d, f.id, session.value, attached.value).length)
    session.value.push(id)
    scriptPanel.value = false
    saveAppData(d, true)
    pickScriptNow(id)
    useEditorStore().openEditor(id)
  }

  /** Closes the dialog. Scripts created in it that no video took: empty ones are deleted, for the others the user decides */
  async function close() {
    dialogs.hide('video')
    form.value = null
    const d = store.getData(), orphans = sessionOrphans(d, session.value)
    session.value = []
    attached.value = []
    scriptPanel.value = false
    if (!orphans.length) return
    const keep: string[] = []
    for (const s of orphans) {
      // uiConfirm escapes the whole message, so the title goes in raw
      if (scriptHasText(s, plain) &&
        !(await uiConfirm(t('The script "{title}" is not linked to any video (the video was not saved).\n\nIf you keep it, you can find it by opening a video › Create script › "Script without video".', { title: tv(s.title) }),
          { title: t('Script without video'), ok: t('Delete the script'), cancel: t('Keep it'), danger: true }))) keep.push(s.id)
    }
    const d2 = store.getData()
    d2.scripts = d2.scripts.filter(s => !orphans.some(o => o.id === s.id) || keep.includes(s.id))
    saveAppData(d2, true)
  }

  /** "Save video" (also "Mark as published" / "Back to the plan" of the published box, and "Find ideas") */
  function save() {
    const f = form.value
    if (!f) return
    const err = formError(f)
    if (err === 'title') {
      // The title is in Details: that tab comes back, and the cursor goes in the title once the message is closed
      missingTitle.value = true
      if (tab.value !== 'details') setTab('details')
      void uiAlertThen(t('Enter at least the video title!')).then(() => { titleFocus.value++ })
      return
    }
    if (err === 'workStart') { uiAlert(t('The work start must come before the end of work (publish day and time).')); return }
    const d = store.getData(), p = pub.value
    const r = saveVideo(d, f, { seo: cleanSeo(seo.value), published: published.value, pub: { links: p.links, at: p.at, note: p.note },
      projectId: projectId(), scripts: [...session.value, ...attached.value] })
    r.video.metrics = cleanMetrics(metrics.value)
    if (r.video.publishDate) useCalendarStore().showDate(r.video.publishDate)   // the Calendar moves to that day
    saveAppData(d)
    void close()
    const title = r.video.title   // syncToast escapes the text
    if (r.pubChanged) syncToast(r.video.published ? t('"{title}" is in Published videos', { title }) : t('"{title}" is back in the plan', { title }))
    else { const msg = celebration(r.before, r.video); if (msg) uiSuccess(msg) }
  }

  /** Delete (two questions: the video, then its scripts). The data is read again after each question */
  async function remove() {
    const vid = form.value?.id
    if (!vid || !(await confirmDelete(t('Are you sure you want to delete this video?')))) return
    let d = store.getData()
    const v = d.videos.find(x => x.id === vid)
    if (!v) return
    const sid = v.scriptId || '', mine = deletedVideoScripts({ scripts: d.scripts, videos: d.videos.filter(x => x.id !== vid) }, vid, sid, session.value)
    let drop = false
    if (mine.length) {
      drop = await uiConfirm(deleteScriptsQuestion(mine.length), { ok: t('Delete'), cancel: tn(mine.length, 'Keep it', 'Keep them'), danger: true })
      d = store.getData()
    }
    deleteVideo(d, vid, session.value, drop)
    if (mine.length) session.value = []
    saveAppData(d)
    void close()
  }

  /** Published box: "Mark as published" (on) / "Back to the plan" saves the video at once with the new state (and closes) */
  function setVideoPublished(on: boolean) {
    published.value = on
    save()
  }

  return { form, tab, tabSeq, session, attached, scriptPanel, scriptsTick, tabs, seo, pub, metrics, published, missingTitle, titleFocus, kind, projectId, open, close, save, remove,
    setTab, videoNow, pickScriptNow, attachScript, createScript, pubVisible, setVideoPublished }
})

/** Opens the video dialog (Vue callers): an existing video, or a new one (null) with an optional Calendar day / time */
export const openVideo = (id: string | null = null, prefill?: VideoPrefill) => useVideoStore().open(id, prefill)

/** "Mark as published" (Board card of the Ready column) and "Back to the plan" (Published videos page): saves and shows
 *  a toast */
export function markPublished(id: string, on = true): void {
  const d = useDataStore().getData(), v = setPublished(d, id, on)
  if (!v) return
  saveAppData(d)
  const title = videoTitle(v)   // syncToast escapes the text
  syncToast(on ? t('"{title}" is in Published videos', { title }) : t('"{title}" is back in the plan', { title }))
}
